<template>
  <div class="trig-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <!-- Grid -->
           <g stroke="rgba(241, 245, 249, 0.05)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>
           
            <!-- Mode 1: 图像模式 / 最值模式 -->
            <g v-if="mode === 'graph' || mode === 'extreme' || mode === 'equation' || mode === 'inverse'">
               <!-- 坐标系 -->
               <line x1="50" y1="300" x2="750" y2="300" stroke="#475569" stroke-width="3" />
               <line x1="400" y1="50" x2="400" y2="550" stroke="#475569" stroke-width="3" />
               
               <!-- 方程模式：目标 Y 值辅助线 -->
               <g v-if="mode === 'equation'">
                  <line x1="50" :y1="300 - targetY * 200" x2="750" :y2="300 - targetY * 200" stroke="#f43f5e" stroke-dasharray="8,4" stroke-width="2" />
                  <text x="60" :y="300 - targetY * 200 - 10" fill="#f43f5e" font-weight="900">y = {{ targetY.toFixed(2) }}</text>
                  
                  <!-- 交点标记 (根) -->
                  <circle v-for="root in roots" :key="'root'+root" :cx="400 + root * 100" :cy="300 - targetY * 200" r="6" fill="#f43f5e" />
               </g>

               <!-- 辅助角模式：原始分路分量 (a sin x, b cos x) -->
               <g v-if="mode === 'extreme'">
                  <path :d="sinePathOnly" fill="none" stroke="rgba(244, 63, 94, 0.3)" stroke-width="2" stroke-dasharray="4,4" />
                  <path :d="cosinePathOnly" fill="none" stroke="rgba(56, 189, 248, 0.3)" stroke-width="2" stroke-dasharray="4,4" />
                  <text x="500" y="100" fill="rgba(244, 63, 94, 0.6)" font-size="12">asin x</text>
                  <text x="500" y="120" fill="rgba(56, 189, 248, 0.6)" font-size="12">bcos x</text>
               </g>

               <!-- 动态主曲线绘制 -->
               <path :d="curvePath" fill="none" :stroke="curveColor" stroke-width="4" />
               
               <!-- 动态探测点 -->
               <circle :cx="400 + currentX * 100" :cy="300 - currentY * 200" r="8" fill="#facc15" stroke="#fff" stroke-width="3" />
            </g>

            <!-- Mode 5: 周期对称性 (Symmetry) -->
            <g v-if="mode === 'symmetry'">
               <g transform="translate(0, 0)">
                  <!-- 基础波形 -->
                  <path :d="curvePath" fill="none" :stroke="curveColor" stroke-width="4" />
                  
                  <!-- 对称轴 (Axis of Symmetry) -->
                  <g v-for="ax in symmetryAxes" :key="'ax'+ax">
                     <line :x1="400 + ax * 100" y1="50" :x2="400 + ax * 100" y2="350" stroke="#facc15" stroke-width="2" stroke-dasharray="8,4" />
                     <text :x="400 + ax * 100 + 5" y="60" fill="#facc15" font-size="10">对称轴</text>
                  </g>
                  
                  <!-- 对称中心 (Center of Symmetry) -->
                  <g v-for="cp in symmetryCenters" :key="'cp'+cp.x">
                     <circle :cx="400 + cp.x * 100" :cy="300 - cp.y * 200" r="6" fill="#10b981" />
                     <text :x="400 + cp.x * 100 + 10" :y="300 - cp.y * 200 - 10" fill="#10b981" font-size="10">对称中心</text>
                  </g>
                  
                  <!-- 周期标注 (Periodicity) -->
                  <g transform="translate(400, 370)">
                     <line x1="0" y1="0" x2="200" y2="0" stroke="#38bdf8" stroke-width="2" />
                     <text x="100" y="-10" fill="#38bdf8" text-anchor="middle" font-size="14" font-weight="900">周期 T = 2π</text>
                  </g>

                  <!-- 动态追踪点 -->
                  <circle :cx="400 + currentX * 100" :cy="300 - currentY * 200" r="10" :fill="curveColor" stroke="#fff" stroke-width="3" />
               </g>
            </g>

            <!-- Mode 4: 换元处理 (Substitution) -->
            <g v-if="mode === 'substitution'">
               <!-- 左侧: t = sin(x) -->
               <g transform="translate(150, 200)">
                  <line x1="-120" y1="0" x2="120" y2="0" stroke="#475569" stroke-width="2" />
                  <line x1="0" y1="-100" x2="0" y2="100" stroke="#475569" stroke-width="2" />
                  <path :d="sinePathSmall" fill="none" stroke="#64748b" stroke-width="1.5" stroke-dasharray="4,2" />
                  <circle :cx="currentX * 30" :cy="-Math.sin(currentX) * 80" r="6" fill="#f43f5e" />
                  <text x="-120" y="-110" fill="#94a3b8" font-size="12">t = sin(x)</text>
               </g>

               <!-- 中间箭头 -->
               <g transform="translate(300, 200)">
                  <path d="M -30 0 L 30 0 L 20 -10 M 30 0 L 20 10" fill="none" stroke="#3b82f6" stroke-width="3" />
                  <text x="-35" y="-20" fill="#3b82f6" font-size="12" font-weight="900">换元 t∈[-1,1]</text>
               </g>

               <!-- 右侧: y = f(t) = t² + 2t -->
               <g transform="translate(450, 200)">
                  <line x1="-120" y1="0" x2="120" y2="0" stroke="#475569" stroke-width="2" />
                  <line x1="0" y1="-120" x2="0" y2="120" stroke="#475569" stroke-width="2" />
                  <!-- 二次曲线区间 [-1, 1] -->
                  <path :d="parabolaPath" fill="none" stroke="#10b981" stroke-width="3" />
                  <circle :cx="Math.sin(currentX) * 80" :cy="-(Math.pow(Math.sin(currentX), 2) + 2*Math.sin(currentAngle)) * 40" r="8" fill="#facc15" stroke="#fff" stroke-width="2" />
                  <text x="-100" y="-130" fill="#10b981" font-size="12">y = t² + 2t (t=sin x)</text>
               </g>
            </g>

            <!-- Mode 3: 辅助角公式 (Extreme) -->
            <g v-if="mode === 'extreme'">
               <g transform="translate(0, 0)">
                  <!-- 分量 1: a sin(x) -->
                  <path :d="sinePathOnly" fill="none" stroke="#f43f5e" stroke-width="2.5" stroke-dasharray="6,4" opacity="0.4" />
                  <!-- 分量 2: b cos(x) -->
                  <path :d="cosinePathOnly" fill="none" stroke="#3b82f6" stroke-width="2.5" stroke-dasharray="6,4" opacity="0.4" />
                  <!-- 合成波 -->
                  <path :d="curvePath" fill="none" stroke="#a855f7" stroke-width="5" stroke-linecap="round" />
                  
                  <!-- 动态指示点 -->
                  <circle :cx="400 + currentX * 100" :cy="300 - currentY * 200" r="10" fill="#facc15" stroke="#fff" stroke-width="3">
                     <animate attributeName="r" values="10;12;10" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <text :x="400 + currentX * 100 + 15" :y="300 - currentY * 200 - 15" fill="#facc15" font-weight="900">P(x, y)</text>
               </g>
            </g>

            <!-- Mode 2: 恒等变换单位圆模式 (Identity) -->
            <g v-if="mode === 'identity'">
               <g transform="translate(400, 300)">
                  <!-- 坐标系与单位圆 -->
                  <line x1="-300" y1="0" x2="300" y2="0" stroke="#475569" stroke-width="3" />
                  <line x1="0" y1="-300" x2="0" y2="300" stroke="#475569" stroke-width="3" />
                  <circle cx="0" cy="0" :r="250" fill="none" stroke="#64748b" stroke-width="3" stroke-dasharray="10,6" />

                  <!-- 场景：三倍角模式 -->
                  <g v-if="subMode === 'triple'">
                     <!-- α -->
                     <line x1="0" y1="0" :x2="250 * Math.cos(-currentAngle)" :y2="250 * Math.sin(-currentAngle)" stroke="#10b981" stroke-width="3" />
                     <circle :cx="250 * Math.cos(-currentAngle)" :cy="250 * Math.sin(-currentAngle)" r="5" fill="#10b981" />
                     <text :x="260 * Math.cos(-currentAngle)" :y="260 * Math.sin(-currentAngle)" fill="#10b981" font-weight="900">α</text>
                     <!-- 3α -->
                     <line x1="0" y1="0" :x2="250 * Math.cos(-3 * currentAngle)" :y2="250 * Math.sin(-3 * currentAngle)" stroke="#f43f5e" stroke-width="5" />
                     <circle :cx="250 * Math.cos(-3 * currentAngle)" :cy="250 * Math.sin(-3 * currentAngle)" r="8" fill="#f43f5e" />
                     <text :x="260 * Math.cos(-3 * currentAngle)" :y="260 * Math.sin(-3 * currentAngle)" fill="#f43f5e" font-weight="900">3α</text>
                  </g>

                  <!-- 场景：积化和差/和差化积 -->
                  <g v-else-if="subMode === 'sum-product'">
                     <!-- 向量 A (angle1) -->
                     <line x1="0" y1="0" :x2="250 * Math.cos(-angle1)" :y2="250 * Math.sin(-angle1)" stroke="#38bdf8" stroke-width="3" />
                     <circle :cx="250 * Math.cos(-angle1)" :cy="250 * Math.sin(-angle1)" r="6" fill="#38bdf8" />
                     <text :x="270 * Math.cos(-angle1)" :y="270 * Math.sin(-angle1)" fill="#38bdf8" font-size="16">sin α</text>
                     <!-- 向量 B (angle2) -->
                     <line x1="0" y1="0" :x2="250 * Math.cos(-angle2)" :y2="250 * Math.sin(-angle2)" stroke="#f43f5e" stroke-width="3" />
                     <circle :cx="250 * Math.cos(-angle2)" :cy="250 * Math.sin(-angle2)" r="6" fill="#f43f5e" />
                     <text :x="270 * Math.cos(-angle2)" :y="270 * Math.sin(-angle2)" fill="#f43f5e" font-size="16">sin β</text>
                     
                     <!-- 和/中位演示 (演示 sinα + sinβ = 2sin((α+β)/2)cos((α-β)/2)) -->
                     <line :x1="250 * Math.cos(-angle1)" :y1="250 * Math.sin(-angle1)" 
                           :x2="250 * Math.cos(-angle1) + 250 * Math.cos(-angle2)" 
                           :y2="250 * Math.sin(-angle1) + 250 * Math.sin(-angle2)" stroke="#94a3b8" stroke-dasharray="4,4" />
                     <line x1="0" y1="0" 
                           :x2="250 * Math.cos(-angle1) + 250 * Math.cos(-angle2)" 
                           :y2="250 * Math.sin(-angle1) + 250 * Math.sin(-angle2)" stroke="#facc15" stroke-width="3" stroke-dasharray="8,4" />
                     <text :x="250 * Math.cos(-angle1) + 250 * Math.cos(-angle2)" :y="250 * Math.sin(-angle1) + 250 * Math.sin(-angle2) - 10" fill="#facc15" font-weight="900" font-size="14">sin α + sin β</text>
                  </g>

                  <!-- 场景：标准恒等 (Standard Identity) -->
                  <g v-else>
                     <!-- 动态角度扫掠区 -->
                     <path :d="`M 0 0 L 100 0 A 100 100 0 0 0 ${100*Math.cos(-currentAngle)} ${100*Math.sin(-currentAngle)} Z`" fill="rgba(56, 189, 248, 0.2)" />
                     <!-- 半径斜边 (Radius = 1) -->
                     <line x1="0" y1="0" :x2="250 * Math.cos(-currentAngle)" :y2="250 * Math.sin(-currentAngle)" stroke="#fff" stroke-width="4" />
                     <!-- x轴投影 (cos) -->
                     <line x1="0" y1="0" :x2="250 * Math.cos(-currentAngle)" y2="0" stroke="#10b981" stroke-width="6" />
                     <!-- y轴投影 (sin) -->
                     <line :x1="250 * Math.cos(-currentAngle)" y1="0" :x2="250 * Math.cos(-currentAngle)" :y2="250 * Math.sin(-currentAngle)" stroke="#f43f5e" stroke-width="6" />
                     <circle :cx="250 * Math.cos(-currentAngle)" :cy="250 * Math.sin(-currentAngle)" r="10" fill="#facc15" stroke="#fff" stroke-width="3" />
                     <text :x="125 * Math.cos(-currentAngle)" :y="25" fill="#10b981" font-size="22" font-weight="900">cos α</text>
                     <text :x="250 * Math.cos(-currentAngle) + 15" :y="125 * Math.sin(-currentAngle)" fill="#f43f5e" font-size="22" font-weight="900">sin α</text>
                  </g>
               </g>
            </g>

        </svg>

        <div class="hud-panel" v-if="mode === 'graph'">
           <div class="hud-item cyan">
              <label>相位角 x</label>
              <span class="val">{{ currentX.toFixed(2) }} <small>rad</small></span>
           </div>
           <div class="hud-item blue">
              <label>函数值 y</label>
              <span class="val">{{ currentY.toFixed(2) }}</span>
           </div>
        </div>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">数学机理解析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前状态计算</div>
               <div class="seq-data-list">
                <template v-if="mode === 'graph' || mode === 'equation'">
                   <div class="data-row"><span>基准函数</span><span class="hl">y = {{ funcType }}(x)</span></div>
                   <div class="data-row"><span>振幅 A</span><span class="hl text-green">{{ paramA.toFixed(1) }}</span></div>
                   <div class="data-row" v-if="mode === 'equation'"><span>目标 Y</span><span class="hl text-red">{{ targetY.toFixed(2) }}</span></div>
                   <div class="data-row" v-if="mode === 'equation'"><span>解的个数 (区间内)</span><span class="hl text-yellow">{{ roots.length }}</span></div>
                </template>
                
                 <template v-if="mode === 'extreme'">
                    <div class="data-row"><span>系数 a (sin sin分量系数)</span><span class="hl text-green">{{ paramA.toFixed(1) }}</span></div>
                    <div class="data-row"><span>系数 b (cos cos分量系数)</span><span class="hl text-blue">{{ paramPhi.toFixed(1) }}</span></div>
                    <div class="data-row"><span>合成振幅 √(a²+b²)</span><span class="hl text-red">{{ Math.sqrt(paramA*paramA + paramPhi*paramPhi).toFixed(2) }}</span></div>
                 </template>

                <template v-if="mode === 'substitution'">
                   <div class="data-row"><span>换元变量 t</span><span class="hl text-red">{{ Math.sin(currentX).toFixed(2) }}</span></div>
                   <div class="data-row"><span>函数值 y (t²+2t)</span><span class="hl text-yellow">{{ (Math.pow(Math.sin(currentX), 2) + 2*Math.sin(currentX)).toFixed(2) }}</span></div>
                   <div class="data-row"><span>t 的取值范围</span><span class="hl">[-1, 1]</span></div>
                </template>

                <template v-if="mode === 'identity'">
                   <div class="data-row"><span>角度 α</span><span class="hl">{{ (currentAngle * 180 / Math.PI).toFixed(1) }}°</span></div>
                   <div class="data-row"><span>纵向投影 (sin α)²</span><span class="hl text-red">{{ Math.pow(Math.sin(currentAngle), 2).toFixed(3) }}</span></div>
                </template>
             </div>
          </div>

          <div class="eq-box mt-l">
             <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ modeDesc }}</p>
          </div>
       </div>
    </div>
  </div>

  <!-- 控制台 -->
  <div class="compact-controls">
    <div class="control-card">
       <div class="control-grid-mini">
          <template v-if="mode === 'graph' || mode === 'extreme' || mode === 'equation' || mode === 'inverse'">
             <div class="s-item">
                <label>场景/函数</label>
                <el-radio-group v-model="funcType" size="small" @change="handleFuncChange">
                  <el-radio-button label="sin" v-if="mode === 'graph' || mode === 'equation'">正弦</el-radio-button>
                  <el-radio-button label="cos" v-if="mode === 'graph' || mode === 'equation'">余弦</el-radio-button>
                  <el-radio-button label="arcsin" v-if="mode === 'inverse'">反正弦</el-radio-button>
                  <el-radio-button label="extreme" v-if="mode === 'extreme'">辅助角叠加</el-radio-button>
                </el-radio-group>
             </div>
             <div class="s-item" v-if="mode === 'equation'">
                <label>方程目标值 k ({{ targetY }})</label>
                <el-slider v-model="targetY" :min="-1" :max="1" :step="0.05" size="small"></el-slider>
             </div>
             <div class="s-item" v-if="mode === 'graph' || mode === 'extreme'">
                <label>{{ mode === 'extreme' ? '系数 a' : '振幅 A' }} ({{ paramA }})</label>
                <el-slider v-model="paramA" :min="0.5" :max="2.5" :step="0.1" size="small"></el-slider>
             </div>
             <div class="s-item" v-if="mode === 'graph'">
                <label>角频率 ω ({{ paramW }})</label>
                <el-slider v-model="paramW" :min="0.5" :max="3" :step="0.5" size="small"></el-slider>
             </div>
             <div class="s-item" v-if="mode === 'graph' || mode === 'extreme'">
                <label>{{ mode === 'extreme' ? '系数 b' : '初相 φ' }} ({{ paramPhi }})</label>
                <el-slider v-model="paramPhi" :min="-3" :max="3" :step="0.1" size="small"></el-slider>
             </div>
               <div class="s-item" style="grid-column: span 2;">
                  <label>X轴扫描播放 / 手动拖拽</label>
                  <el-slider v-model="drawProgress" :min="0" :max="100" size="small"></el-slider>
               </div>
            </template>
            
            <template v-if="mode === 'identity'">
               <div class="s-item" style="grid-column: span 3;">
                  <label>旋转角度 α (0 ~ 2π)</label>
                  <el-slider v-model="currentAngle" :min="0" :max="6.28" :step="0.01" size="small"></el-slider>
               </div>
            </template>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrigFunctionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'graph',
      subMode: 'normal',
      funcType: 'sin',
      paramA: 1,
      paramW: 1,
      paramPhi: 0,
      drawProgress: 50,
      currentAngle: 0.785,
      angle2: 0.4, // For sum-product
      targetY: 0.5 
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    angle1() { return this.currentAngle; },
    topicIntro() {
       if (this.mode === 'symmetry') return '周期性与对称性：探究三角函数的波形规律，利用对称轴与中心简化计算';
       if (this.mode === 'substitution') return '换元处理：通过 t = sin x 将超越函数转化为代数多项式探究最值';
       if (this.mode === 'identity') {
          if (this.subMode === 'triple') return '三倍角公式：探究 3α 与 α 在单位圆上的旋转关系';
          if (this.subMode === 'sum-product') return '和差化积：探究两个不同相位向量叠加后的数学规律';
          return '三角恒等变换：单位圆内勾股定理的代数抽象';
       }
       if (this.mode === 'inverse') return '反三角函数：限制定义域以获得反函数的几何镜像映射';
       if (this.mode === 'extreme') return '辅助角公式：探究 a·sin x + b·cos x 的合成振幅与极值';
       if (this.mode === 'equation') return '三角方程：寻找周期函数与水平直线的交点规律';
       return '三角函数的图象与性质：周期运动的数学刻画';
    },
    activeFormula() {
       if (this.mode === 'symmetry') return 'f(x+T) = f(x), f(x₀+x) = f(x₀-x)';
       if (this.mode === 'substitution') return 'y = (sin x)² + 2 sin x';
       if (this.mode === 'identity') {
          if (this.subMode === 'triple') return 'sin 3α = 3sin α - 4sin³α';
          if (this.subMode === 'sum-product') return 'sin α + sin β = 2sin((α+β)/2)cos((α-β)/2)';
          return 'sin²α + cos²α = 1';
       }
       if (this.mode === 'inverse') return 'y = ' + this.funcType + '(x)';
       if (this.mode === 'extreme') return 'asin x + bcos x = √(a²+b²)sin(x+φ)';
       if (this.mode === 'equation') return 'sin(x) = k';
       return 'y = A·' + this.funcType + '(ωx + φ)';
    },
    symmetryAxes() {
       // sin(x) axes at x = kPI + PI/2
       if (this.funcType === 'sin') return [-Math.PI/2, Math.PI/2];
       if (this.funcType === 'cos') return [0, -Math.PI, Math.PI];
       return [];
    },
    symmetryCenters() {
       // sin(x) centers at kPI
       if (this.funcType === 'sin') return [{x: 0, y:0}, {x: Math.PI, y:0}, {x: -Math.PI, y:0}];
       if (this.funcType === 'cos') return [{x: Math.PI/2, y:0}, {x: -Math.PI/2, y:0}];
       return [];
    },
    curveColor() {
       if (this.mode === 'extreme') return '#a855f7';
       return this.funcType === 'sin' ? '#f43f5e' : '#38bdf8';
    },
    currentX() {
       return Math.PI * (this.drawProgress/50 - 1); // maps 0-100 to -PI to +PI
    },
    currentY() {
       const x = this.currentX;
       if (this.mode === 'extreme') return this.paramA * Math.sin(x) + this.paramPhi * Math.cos(x);
       if (this.funcType === 'sin') return this.paramA * Math.sin(this.paramW * x + this.paramPhi);
       if (this.funcType === 'cos') return this.paramA * Math.cos(this.paramW * x + this.paramPhi);
       if (this.funcType === 'arcsin') return Math.abs(x) <= 1 ? Math.asin(x) : 0;
       if (this.funcType === 'arccos') return Math.abs(x) <= 1 ? Math.acos(x) : 0;
       return 0;
    },
    sinePathSmall() {
       let pts = [];
       for (let px = -4; px <= 4; px += 0.2) { pts.push(`${px * 30},${-Math.sin(px) * 80}`); }
       return 'M ' + pts.join(' L ');
    },
    parabolaPath() {
       let pts = [];
       // f(t) = t^2 + 2t in t in [-1, 1]
       for (let pt = -1; pt <= 1.05; pt += 0.1) {
          let py = pt * pt + 2 * pt;
          pts.push(`${pt * 80},${-py * 40}`);
       }
       return 'M ' + pts.join(' L ');
    },
    roots() {
       if (this.mode !== 'equation') return [];
       const r1 = Math.asin(this.targetY);
       const r2 = Math.PI - r1;
       const r3 = -Math.PI - r1;
       return [r1, r2, r3].filter(r => r >= -3.5 && r <= 3.5);
    },
    sinePathOnly() {
       let pts = [];
       const a = this.mode === 'extreme' ? this.paramA : 1.0;
       for (let px = -3.5; px <= 3.5; px += 0.05) { pts.push(`${400 + px * 100},${300 - a * Math.sin(px) * 200}`); }
       return 'M ' + pts.join(' L ');
    },
    cosinePathOnly() {
       let pts = [];
       const b = this.mode === 'extreme' ? this.paramPhi : 0.8;
       for (let px = -3.5; px <= 3.5; px += 0.05) { pts.push(`${400 + px * 100},${300 - b * Math.cos(px) * 200}`); }
       return 'M ' + pts.join(' L ');
    },
    curvePath() {
       let pts = [];
       if (this.mode === 'inverse') {
          for (let px = -1; px <= 1; px += 0.02) {
             let py = this.funcType === 'arcsin' ? Math.asin(px) : Math.acos(px);
             pts.push(`${400 + px * 100},${300 - py * 100}`); 
          }
       } else if (this.mode === 'extreme') {
           for (let px = -3.5; px <= 3.5; px += 0.05) {
              let py = this.paramA * Math.sin(px) + this.paramPhi * Math.cos(px);
              pts.push(`${400 + px * 100},${300 - py * 200}`);
           }
       } else {
           for (let px = -3.5; px <= 3.5; px += 0.05) {
              let py = 0;
              if (this.funcType === 'sin') py = this.paramA * Math.sin(this.paramW * px + this.paramPhi);
              else py = this.paramA * Math.cos(this.paramW * px + this.paramPhi);
              pts.push(`${400 + px * 100},${300 - py * 200}`);
           }
       }
       return 'M ' + pts.join(' L ');
    },
    modeDesc() {
       if (this.mode === 'symmetry') return '三角函数具有极强的几何对称性。正弦波关于 x = kπ + π/2 对称，余弦波关于 y 轴对称。利用这些性质，我们可以将研究范围从全域缩小到最简单调区间，从而轻松获求函数的最值情况。';
       if (this.mode === 'substitution') return '换元法的本质是将复杂的三角问题“退化”为简单的代数问题。通过 t = sin x，我们将一个周期波动问题映射到了二次函数的确定域 [-1, 1] 上，从而利用抛物线的单调性求得最值。';
       if (this.subMode === 'triple') return '三倍角公式展示了当初始角 α 扩大三倍后，终边落在单位圆上的新位置。这在解代数方程和多项式化简中极为重要。';
       if (this.subMode === 'sum-product') return '通过向量叠加演示和差化积。sin α + sin β 的值等于两个向量合成后纵轴分量的长度。这直观展示了波的干涉原理。';
       if (this.mode === 'equation') return '三角方程的本质是波形与直线的交点规律。';
       if (this.mode === 'extreme') return '辅助角公式将不同波合称为单波。';
       return '这是三角函数最基础的单位圆表现形式。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      this.subMode = 'normal';
      if (/周期|对称/.test(kp)) {
         this.mode = 'symmetry';
      } else if (/换元/.test(kp)) {
         this.mode = 'substitution';
      } else if (/反三角/.test(kp)) {
         this.mode = 'inverse'; this.funcType = 'arcsin';
      } else if (/方程/.test(kp)) {
         this.mode = 'equation'; this.funcType = 'sin';
      } else if (/最值|辅助角/.test(kp)) {
         this.mode = 'extreme';
      } else if (/三倍角/.test(kp)) {
         this.mode = 'identity'; this.subMode = 'triple';
      } else if (/和差化积|积化和差/.test(kp)) {
         this.mode = 'identity'; this.subMode = 'sum-product';
      } else if (/恒等变换|公式|和差|降幂/.test(kp)) {
         this.mode = 'identity';
      } else {
         this.mode = 'graph';
      }
    },
    handleFuncChange() {
       this.drawProgress = 50; 
    }
  }
}
</script>

<style scoped lang="scss">
.trig-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { 
   display: flex; 
   justify-content: space-between; 
   align-items: center; 
   padding: 0 4px; 
   .brief { font-size: 15px; color: #1e293b; font-weight: 700; flex-shrink: 1; } 
   .formula-chip { 
      background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; 
      padding: 6px 14px; border-radius: 6px; font-weight: 900; 
      font-family: -apple-system, sans-serif; font-size: 16px; 
      white-space: nowrap; flex-shrink: 0; min-width: min-content;
   } 
}
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 15px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 16px; color: #f8fafc; font-weight: 900; font-family: monospace; small { font-size: 12px; font-weight: normal; color: #cbd5e1; } } &.cyan { border-left: 4px solid #06b6d4; } &.blue { border-left: 4px solid #3b82f6; } } } }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
